class TIMLParseError extends Error {
  constructor(message, lineNumber, lineText, column = null) {
    const details = lineText ? ` (line ${lineNumber})` : ` (line ${lineNumber})`;
    const snippet = lineText
      ? `\n${lineText}\n${' '.repeat(Math.max(0, (column || 1) - 1))}^`
      : '';
    super(`TIML parse error${details}: ${message}${snippet}`);
    this.name = 'TIMLParseError';
    this.lineNumber = lineNumber;
    this.lineText = lineText;
    this.column = column;
  }
}

export default class TIMLParser {
  static INDENT = 2;

  static parse(view) {
    const template = view?.timl ?? view?.html;
    if (!view || typeof template !== 'string') {
      throw new Error('TIMLParser.parse expected a view with a timl string.');
    }

    const lines = String(template).split('\n');
    const firstNonEmpty = lines.find(line => line.trim().length) || '';
    const baseIndent = this.countLeadingSpaces(firstNonEmpty);
    const root = { type: 'root', children: [] };
    const stack = [{ level: -1, node: root }];

    for (let index = 0; index < lines.length; index++) {
      const rawLine = lines[index];
      const trimmedLine = rawLine.trim();
      const lineNumber = index + 1;

      if (!trimmedLine) continue;
      this.validateIndentation(rawLine, trimmedLine, lineNumber, baseIndent);

      const indentLevel = Math.max(0, (this.countLeadingSpaces(rawLine) - baseIndent) / this.INDENT);
      const parentEntry = stack[stack.length - 1];
      if (indentLevel > parentEntry.level + 1) {
        throw new TIMLParseError('indentation jumped too deep', lineNumber, trimmedLine, 1);
      }

      while (stack.length && stack[stack.length - 1].level >= indentLevel) {
        stack.pop();
      }

      const currentParent = stack[stack.length - 1]?.node;
      if (!currentParent) {
        throw new TIMLParseError('could not determine parent node from indentation', lineNumber, trimmedLine, 1);
      }

      if (trimmedLine === 'html:') {
        const rawHtmlBlock = this.collectRawHtmlBlock(lines, index, baseIndent);
        if (!rawHtmlBlock.html.trim()) {
          throw new TIMLParseError('html: blocks must include indented raw HTML', lineNumber, trimmedLine, 1);
        }

        currentParent.children.push({
          type: 'raw_html',
          html: rawHtmlBlock.html,
          lineNumber
        });
        index = rawHtmlBlock.lastLineIndex;
        continue;
      }

      const node = this.parseLine(trimmedLine, lineNumber);
      currentParent.children.push(node);

      if (node.type === 'element') {
        stack.push({ level: indentLevel, node });
      }
    }

    return root;
  }

  static validateIndentation(rawLine, trimmedLine, lineNumber, baseIndent) {
    if (rawLine.includes('\t')) {
      throw new TIMLParseError('tabs are not supported; use spaces', lineNumber, trimmedLine, 1);
    }

    const leadingSpaces = this.countLeadingSpaces(rawLine);
    const relativeIndent = leadingSpaces - baseIndent;
    if (relativeIndent < 0) {
      throw new TIMLParseError('indentation is shallower than the template baseline', lineNumber, trimmedLine, 1);
    }

    if (relativeIndent % this.INDENT !== 0) {
      throw new TIMLParseError(`indentation must use ${this.INDENT}-space increments`, lineNumber, trimmedLine, 1);
    }
  }

  static collectRawHtmlBlock(lines, startIndex, baseIndent) {
    const parentIndent = this.countLeadingSpaces(lines[startIndex]);
    const htmlLines = [];
    let lastLineIndex = startIndex;
    let blockIndent = null;

    for (let index = startIndex + 1; index < lines.length; index++) {
      const rawLine = lines[index];
      const trimmedLine = rawLine.trim();

      if (!trimmedLine) {
        htmlLines.push('');
        lastLineIndex = index;
        continue;
      }

      this.validateIndentation(rawLine, trimmedLine, index + 1, baseIndent);
      const leadingSpaces = this.countLeadingSpaces(rawLine);
      if (leadingSpaces <= parentIndent) {
        break;
      }

      if (blockIndent == null) {
        blockIndent = leadingSpaces;
      }

      htmlLines.push(rawLine.slice(blockIndent));
      lastLineIndex = index;
    }

    return {
      html: htmlLines.join('\n'),
      lastLineIndex
    };
  }

  static parseLine(line, lineNumber) {
    if (line.startsWith('</')) {
      throw new TIMLParseError('closing tags are not used in TIML; fix indentation instead', lineNumber, line, 1);
    }

    if (line.startsWith('<')) {
      const componentNode = this.parseAngleComponentLine(line, lineNumber);
      if (componentNode) return componentNode;

      return {
        type: 'raw_html',
        html: line,
        lineNumber
      };
    }

    const normalizedLine = line.startsWith('.') || line.startsWith('#') ? `div${line}` : line;
    return this.parseElementLine(normalizedLine, lineNumber);
  }

  static parseAngleComponentLine(line, lineNumber) {
    const componentMatch = line.match(/^<([A-Z][\w-]*)([^>]*)\/?>$/);
    if (!componentMatch) return null;

    const [, name, rawAttrs = ''] = componentMatch;
    const attrSource = rawAttrs.replace(/\/\s*$/, '').trim();

    return {
      type: 'component',
      name,
      attrs: attrSource ? this.parseAttributes(attrSource, lineNumber, line) : {},
      children: [],
      lineNumber
    };
  }

  static parseElementLine(line, lineNumber) {
    let cursor = 0;
    const tag = this.consumeIdentifier(line, cursor, /[A-Za-z][\w-]*/y, 'tag name', lineNumber);
    cursor += tag.length;

    const node = {
      type: 'element',
      tag,
      id: null,
      classes: [],
      attrs: {},
      text: '',
      children: [],
      lineNumber
    };

    while (cursor < line.length) {
      const char = line[cursor];

      if (char === '#') {
        cursor++;
        node.id = this.consumeIdentifier(line, cursor, /[\w-]+/y, 'id', lineNumber);
        cursor += node.id.length;
        continue;
      }

      if (char === '.') {
        cursor++;
        const className = this.consumeIdentifier(line, cursor, /[\w/:-]+/y, 'class name', lineNumber);
        node.classes.push(className);
        cursor += className.length;
        continue;
      }

      if (char === '[') {
        const bracket = this.consumeBracketBlock(line, cursor, lineNumber);
        Object.assign(node.attrs, this.parseAttributes(bracket.content, lineNumber, line));
        cursor = bracket.nextCursor;
        continue;
      }

      if (char === ' ') {
        node.text = line.slice(cursor + 1);
        return node;
      }

      throw new TIMLParseError(`unexpected character "${char}"`, lineNumber, line, cursor + 1);
    }

    return node;
  }

  static consumeIdentifier(line, cursor, pattern, label, lineNumber) {
    pattern.lastIndex = cursor;
    const match = pattern.exec(line);
    if (!match) {
      throw new TIMLParseError(`expected ${label}`, lineNumber, line, cursor + 1);
    }
    return match[0];
  }

  static consumeBracketBlock(line, cursor, lineNumber) {
    let index = cursor + 1;
    let quote = null;

    while (index < line.length) {
      const char = line[index];
      if (quote) {
        if (char === quote && line[index - 1] !== '\\') quote = null;
      } else if (char === '"' || char === "'") {
        quote = char;
      } else if (char === ']') {
        return {
          content: line.slice(cursor + 1, index),
          nextCursor: index + 1
        };
      }
      index++;
    }

    throw new TIMLParseError('missing closing ] for attribute block', lineNumber, line, cursor + 1);
  }

  static parseAttributes(content, lineNumber, originalLine) {
    const attrs = {};
    let cursor = 0;

    while (cursor < content.length) {
      while (content[cursor] === ' ') cursor++;
      if (cursor >= content.length) break;

      const name = this.consumeIdentifier(content, cursor, /[\w:-]+/y, 'attribute name', lineNumber);
      cursor += name.length;

      while (content[cursor] === ' ') cursor++;

      if (content[cursor] !== '=') {
        attrs[name] = '';
        continue;
      }

      cursor++;
      while (content[cursor] === ' ') cursor++;

      const quote = content[cursor];
      if (quote !== '"' && quote !== "'") {
        throw new TIMLParseError(`attribute "${name}" must use quoted values`, lineNumber, originalLine, cursor + 1);
      }

      cursor++;
      let value = '';
      let closed = false;
      while (cursor < content.length) {
        const char = content[cursor];
        if (char === quote && content[cursor - 1] !== '\\') {
          cursor++;
          closed = true;
          break;
        }
        value += char;
        cursor++;
      }

      if (!closed) {
        throw new TIMLParseError(`attribute "${name}" is missing a closing quote`, lineNumber, originalLine, cursor + 1);
      }

      attrs[name] = value;
    }

    return attrs;
  }

  static countLeadingSpaces(line) {
    return (line.match(/^[ ]*/)?.[0] || '').length;
  }
}

export { TIMLParseError };
